/**
 * ENG-6123 — flap dampener for a managed agent's "desired config set".
 *
 * Incident (valai, prod-gold-coast-city-marina-gccm-host, 2026-06-08): two
 * Composio integrations (Meta Ads, Google Analytics) had their `status` column
 * oscillate in and out of `active`/`configured` roughly every 60s. Each flip
 * changed the membership the API returned, so the manager rewrote `.mcp.json`
 * with a different server set and (a) injected an `integration-update` /
 * `mcp-update` notice the opus session wrote a full reply to, and (b) scheduled
 * a session restart that reloaded the entire system prompt. Both are pure
 * input-token burn with zero task output — ~1,550 fresh opus sessions in 4 days.
 *
 * The existing guards did NOT catch this:
 *   - The `.mcp.json` drift comparison is already canonical (ENG-5071) and
 *     membership-aware (ENG-5537) — it correctly reported *genuine* membership
 *     changes, because the set really was oscillating.
 *   - The ENG-5441 restart breaker only counts restarts that actually PROCEED;
 *     while the maintenance window deferred them they weren't counted, and the
 *     notice injection (the dominant burn) is not gated by the breaker at all.
 *
 * This dampener closes that gap. It records the sequence of membership
 * "signatures" the manager observes for an agent on a given channel
 * (integration set / managed-MCP structure) and reports when that sequence is
 * *oscillating* — flipping back and forth between two or more distinct states
 * many times in a short window, as opposed to changing once (a legitimate
 * add/remove). While an agent/channel is flapping, the caller suppresses the
 * notice injection and the restart, emits a single throttled warning + the
 * config-churn metric, and only resumes normal hot-reload once the set has held
 * steady for the cool-off window.
 *
 * Pure state, no I/O — the caller owns when to record and what to suppress, so
 * this stays unit-testable with an injected clock (mirrors restart-breaker.ts).
 */

// ENG-8359: the provisioning writer's own per-connection key rule, so the
// orphan reaper's expected set cannot drift from what was actually written.
import { remoteMcpServerKey } from '@augmented/core/integrations';

export interface FlapDampenerOptions {
  /** Sliding window for counting transitions. Default 600_000 (10 min). env: AGT_MCP_FLAP_WINDOW_MS */
  windowMs?: number;
  /**
   * Number of *transitions* (consecutive observations whose signature differs
   * from the prior one) inside the window that marks the agent/channel as
   * flapping. A one-shot A→B change is 1 transition; an A→B→A→B oscillation is
   * 3. Default 4 — comfortably above any legitimate burst of real edits.
   * env: AGT_MCP_FLAP_MIN_TRANSITIONS
   */
  minTransitions?: number;
  /**
   * Distinct signatures required in-window before flapping can be declared. A
   * monotonic sequence of unique states (A→B→C→D, e.g. an operator adding
   * toolkits one at a time) has many transitions but should NOT dampen; an
   * oscillation revisits a prior state. Default 2; combined with a revisit
   * check below, this keeps strictly-monotonic growth from tripping.
   * env: AGT_MCP_FLAP_MIN_DISTINCT
   */
  minDistinct?: number;
  /**
   * ENG-6512: a second, longer-horizon window for catching a *slow* oscillation.
   * The fast tier above counts membership flips inside `windowMs` (10 min) and
   * needs `minTransitions` (4) of them — i.e. roughly one flip every 2.5 min
   * sustained. valai's residual churn on gccm-host flipped only ~4 times across
   * ~41 min (04:12/04:42/04:48/04:53, 2026-06-15), so no 10-min window ever held
   * 4 transitions and the fast tier never tripped, even though the set was
   * genuinely oscillating (Drive/Analytics/Ads cycling in and out — a revisiting
   * pattern, not a monotonic operator edit). This slow tier counts flips inside
   * `longWindowMs` (60 min) and trips at `longMinTransitions` (4), 6× more
   * sensitive to a slow flap while keeping the same revisit gate so a monotonic
   * one-shot operator add/remove still never dampens.
   * Default 3_600_000 (60 min). env: AGT_MCP_FLAP_LONG_WINDOW_MS
   */
  longWindowMs?: number;
  /**
   * Transitions inside `longWindowMs` that mark the channel as flapping on the
   * slow tier. Default 4. env: AGT_MCP_FLAP_LONG_MIN_TRANSITIONS
   */
  longMinTransitions?: number;
  /** Injectable clock for tests. */
  now?: () => number;
}

export interface FlapDecision {
  /** True when the channel is currently oscillating and the caller should suppress. */
  flapping: boolean;
  /**
   * Distinct signatures seen in-window (post-record). Reports the tier that
   * drove the current suppression (slow tier when only it tripped), else the
   * fast 10-min window — so the onset log line explains the actual trip.
   */
  distinctStates: number;
  /** Transitions seen in-window (post-record). Tier-of-record, as above. */
  transitions: number;
  /** Total observations retained across the widest (slow) window (post-record). */
  windowCount: number;
  /**
   * True only on the observation that flips this channel closed→flapping — lets
   * the caller log/emit the churn event exactly once per flap onset rather than
   * every poll.
   */
  onset: boolean;
  /**
   * ENG-6512: which tier declared the oscillation on this observation — 'fast'
   * (10-min density), 'slow' (60-min slow flap), or null when not oscillating.
   * The fast tier wins when both trip. Lets the onset log name the tier.
   */
  tier: 'fast' | 'slow' | null;
  /** ENG-6512: transitions seen across the slow (60-min) window (post-record). */
  longTransitions: number;
  /** ENG-6512: distinct signatures across the slow (60-min) window (post-record). */
  longDistinctStates: number;
}

interface Observation {
  sig: string;
  at: number;
}

/** Transition / distinct-state / revisit metrics for one sub-window. */
interface WindowMetrics {
  transitions: number;
  distinctStates: number;
  revisited: boolean;
}

const DEFAULT_WINDOW_MS = 600_000;
const DEFAULT_MIN_TRANSITIONS = 4;
const DEFAULT_MIN_DISTINCT = 2;
const DEFAULT_LONG_WINDOW_MS = 3_600_000;
const DEFAULT_LONG_MIN_TRANSITIONS = 4;

export function readEnvNumber(name: string, fallback: number): number {
  const raw = process.env[name];
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Canonical signature for a desired membership set — sorted, de-duplicated, and
 * joined so insertion order / serialization shape never registers as a change
 * (the same invariant the ENG-5071 canonical-JSON hash gives the drift check).
 */
export function membershipSignature(keys: Iterable<string>): string {
  return [...new Set(keys)].sort().join(',');
}

/**
 * Per-agent, per-channel flap detector. One instance per manager; keyed
 * internally by `${codeName}\0${channel}` so an agent's integration-set flap and
 * managed-MCP-structure flap are tracked independently. Held in module-level
 * state in manager-worker.ts so it survives across polls within a worker
 * generation. Trip state is intentionally NOT persisted — a manager restart
 * starts a fresh window, same posture as the restart breaker's event log.
 */
export class McpFlapDampener {
  private readonly windowMs: number;
  private readonly minTransitions: number;
  private readonly minDistinct: number;
  private readonly longWindowMs: number;
  private readonly longMinTransitions: number;
  private readonly now: () => number;
  private readonly history = new Map<string, Observation[]>();
  private readonly flapping = new Set<string>();
  // ENG-7239: timestamp of the most recent poll on which each (agent, channel)
  // was flapping. Drives `flappedWithin` so the caller can exempt a managed-MCP
  // churn restart from the ENG-5441 breaker for a short cool-off after a flap;
  // see that method for the Kylie/dig (2026-06-29) rationale.
  private readonly lastFlapAt = new Map<string, number>();

  constructor(opts: FlapDampenerOptions = {}) {
    this.windowMs = opts.windowMs ?? readEnvNumber('AGT_MCP_FLAP_WINDOW_MS', DEFAULT_WINDOW_MS);
    this.minTransitions = opts.minTransitions ?? readEnvNumber('AGT_MCP_FLAP_MIN_TRANSITIONS', DEFAULT_MIN_TRANSITIONS);
    this.minDistinct = opts.minDistinct ?? readEnvNumber('AGT_MCP_FLAP_MIN_DISTINCT', DEFAULT_MIN_DISTINCT);
    this.longWindowMs = opts.longWindowMs ?? readEnvNumber('AGT_MCP_FLAP_LONG_WINDOW_MS', DEFAULT_LONG_WINDOW_MS);
    this.longMinTransitions =
      opts.longMinTransitions ?? readEnvNumber('AGT_MCP_FLAP_LONG_MIN_TRANSITIONS', DEFAULT_LONG_MIN_TRANSITIONS);
    this.now = opts.now ?? Date.now;
    if (!Number.isFinite(this.windowMs) || this.windowMs < 1000) {
      throw new Error('mcp-flap-dampener windowMs must be a finite number >= 1000');
    }
    if (!Number.isFinite(this.minTransitions) || this.minTransitions < 2) {
      throw new Error('mcp-flap-dampener minTransitions must be a finite number >= 2');
    }
    if (!Number.isFinite(this.minDistinct) || this.minDistinct < 2) {
      throw new Error('mcp-flap-dampener minDistinct must be a finite number >= 2');
    }
    if (!Number.isFinite(this.longWindowMs) || this.longWindowMs < this.windowMs) {
      throw new Error('mcp-flap-dampener longWindowMs must be a finite number >= windowMs');
    }
    if (!Number.isFinite(this.longMinTransitions) || this.longMinTransitions < 2) {
      throw new Error('mcp-flap-dampener longMinTransitions must be a finite number >= 2');
    }
  }

  private key(codeName: string, channel: string): string {
    return `${codeName}\0${channel}`;
  }

  /**
   * Record an observed membership signature for (codeName, channel) and return
   * the current flap decision. Call this on every poll where the manager has
   * computed the desired set — including unchanged polls — so a set that has
   * gone quiet ages out of the window and the channel recovers.
   */
  record(codeName: string, channel: string, signature: string): FlapDecision {
    const k = this.key(codeName, channel);
    const at = this.now();
    // Retain history across the WIDEST window (the slow tier) so both tiers can
    // be computed from one buffer; the fast tier just slices a recent sub-window.
    const cutoff = at - this.longWindowMs;
    const prior = (this.history.get(k) ?? []).filter((o) => o.at >= cutoff);
    prior.push({ sig: signature, at });
    this.history.set(k, prior);

    const fast = this.windowMetrics(prior, at - this.windowMs);
    const slow = this.windowMetrics(prior, at - this.longWindowMs);

    // Two tiers, same revisit gate. Fast = dense flap in 10 min; slow = a flap
    // sustained over 60 min that the fast window is too short to see (ENG-6512,
    // valai's ~4-flips-in-41-min cadence). Either tripping dampens.
    const fastOsc =
      fast.transitions >= this.minTransitions && fast.distinctStates >= this.minDistinct && fast.revisited;
    const slowOsc =
      slow.transitions >= this.longMinTransitions && slow.distinctStates >= this.minDistinct && slow.revisited;
    const oscillating = fastOsc || slowOsc;
    const tier: 'fast' | 'slow' | null = fastOsc ? 'fast' : slowOsc ? 'slow' : null;

    // The current poll changed the set (vs the immediately-prior poll). A poll
    // where the set held steady is a "stable step" — the recovery signal.
    const transitionNow = prior.length >= 2 && prior[prior.length - 1]!.sig !== prior[prior.length - 2]!.sig;

    const wasFlapping = this.flapping.has(k);
    let onset = false;
    if (wasFlapping) {
      // Recover on the first stable step — the set held steady this poll — even
      // if older transitions still sit inside the window. Don't wait for them
      // to age out: an operator who just stabilised the config (the valai fix:
      // deleting the two flapping integrations) should see suppression lift on
      // the very next quiet poll, not a full window later.
      if (!transitionNow) {
        this.flapping.delete(k);
        // Collapse history to just this stable observation. Without it, the
        // now-stale oscillation still sits in the (up to 60-min) slow window,
        // so the NEXT genuine one-shot operator change would revisit a prior
        // signature and re-trip the slow tier — re-suppressing a legitimate
        // edit. A genuinely renewed flap simply re-accumulates from this clean
        // baseline (ENG-6512 / CodeRabbit PR #2228).
        this.history.set(k, [{ sig: signature, at }]);
      }
    } else if (transitionNow && oscillating) {
      this.flapping.add(k);
      onset = true;
    }

    // ENG-7239: stamp the last-flap time on every poll the channel is (still)
    // flapping, so `flappedWithin` keeps guarding the breaker across the eager
    // single-poll recovery (ENG-6512) that can momentarily clear `flapping`
    // mid-oscillation.
    const nowFlapping = this.flapping.has(k);
    if (nowFlapping) this.lastFlapAt.set(k, at);

    // Report the tripping tier's metrics so the onset log explains the actual
    // suppression; fall back to the fast window when not oscillating.
    const reported = tier === 'slow' ? slow : fast;
    return {
      flapping: nowFlapping,
      distinctStates: reported.distinctStates,
      transitions: reported.transitions,
      windowCount: prior.length,
      onset,
      tier,
      longTransitions: slow.transitions,
      longDistinctStates: slow.distinctStates,
    };
  }

  /**
   * Transitions / distinct signatures / revisit flag for the observations that
   * fall inside `[windowStart, now]`. The latest observation (the one just
   * recorded) is always in-window. A revisit = the latest signature appears
   * earlier in the sub-window but NOT as the immediately-preceding observation,
   * so strictly-monotonic growth (A→B→C→D) never counts as a revisit.
   */
  private windowMetrics(prior: Observation[], windowStart: number): WindowMetrics {
    const inWin = prior.filter((o) => o.at >= windowStart);
    let transitions = 0;
    for (let i = 1; i < inWin.length; i++) {
      if (inWin[i]!.sig !== inWin[i - 1]!.sig) transitions++;
    }
    const distinctStates = new Set(inWin.map((o) => o.sig)).size;
    const latest = inWin[inWin.length - 1];
    const revisited = latest
      ? inWin.slice(0, -1).some((o, i) => o.sig === latest.sig && i !== inWin.length - 2)
      : false;
    return { transitions, distinctStates, revisited };
  }

  /** True if (codeName, channel) is currently dampened. */
  isFlapping(codeName: string, channel: string): boolean {
    return this.flapping.has(this.key(codeName, channel));
  }

  /**
   * ENG-7239: true if (codeName, channel) was flapping within the last
   * `withinMs`. Lets the caller exempt a managed-MCP-churn session restart from
   * the ENG-5441 circuit breaker during (and for a short cool-off after) a
   * flap episode.
   *
   * Why a cool-off and not just `isFlapping`: the dampener recovers eagerly, on
   * the first stable poll (ENG-6512), so during an ACTIVE oscillation a single
   * transient poll where the desired set momentarily repeats clears `flapping`
   * for that instant. If the very next poll flips again, that flip's restart
   * would (pre-fix) be treated as a genuine change and counted toward the
   * breaker; three such flips in 10 min auto-paused Kylie (org dig) on
   * 2026-06-29 and left her unreachable on every channel. Keying the breaker
   * exemption off "flapped within the cool-off" instead of the exact-instant
   * `isFlapping` closes that race. Mirrors ENG-5547's single-dead-MCP reaper
   * exemption ("restart, but don't count toward the agent-wide breaker").
   */
  flappedWithin(codeName: string, channel: string, withinMs: number): boolean {
    const at = this.lastFlapAt.get(this.key(codeName, channel));
    return at !== undefined && this.now() - at <= withinMs;
  }

  /** Drop all state for an agent (called on deprovision / agent removal). */
  forget(codeName: string): void {
    const prefix = `${codeName}\0`;
    for (const k of [...this.history.keys()]) {
      if (k.startsWith(prefix)) this.history.delete(k);
    }
    for (const k of [...this.flapping]) {
      if (k.startsWith(prefix)) this.flapping.delete(k);
    }
    for (const k of [...this.lastFlapAt.keys()]) {
      if (k.startsWith(prefix)) this.lastFlapAt.delete(k);
    }
  }
}

/** Channel identifiers for the two managed-config sources the manager hot-reloads. */
export const FLAP_CHANNEL_INTEGRATIONS = 'integrations';
export const FLAP_CHANNEL_MANAGED_MCP = 'managed-mcp';

/**
 * ENG-7239: default cool-off for `flappedWithin` when exempting a managed-MCP
 * churn restart from the ENG-5441 breaker. Defaults to the fast-tier window
 * (10 min), long enough to bridge the eager-recovery race for the whole flap
 * episode, short enough that a genuine restart well after the set settles still
 * counts. Override via AGT_MCP_FLAP_BREAKER_COOLDOWN_MS.
 */
export const DEFAULT_FLAP_BREAKER_COOLDOWN_MS = 600_000;

/**
 * Managed-MCP server-key prefixes. A .mcp.json server whose key starts with one
 * of these is a brokered/managed toolkit server (Composio and friends), as
 * opposed to a channel listener (slack/telegram/direct-chat) or the core
 * augmented server. Shared by the manager's stale-server prune and the ENG-7239
 * breaker-exemption scoping so the two classifications cannot drift apart.
 */
export const MANAGED_MCP_SERVER_PREFIXES = [
  'composio_', 'one_', 'pipedream_', 'nango_', 'paragon_',
  'composio-', 'one-', 'pipedream-', 'nango-', 'paragon-',
] as const;

/** True if a .mcp.json server key belongs to the managed-MCP (brokered toolkit) set. */
export function isManagedMcpServerKey(key: string): boolean {
  return MANAGED_MCP_SERVER_PREFIXES.some((p) => key.startsWith(p));
}

/**
 * A remote MCP server entry: HTTP/SSE transport (an explicit `type` of
 * `http`/`sse`, or a `url` with no `command`). stdio servers, channels
 * (slack/telegram/direct-chat) and native integrations (buildNativeMcpEntry)
 * all carry a `command` and are NOT remote.
 *
 * ENG-7994: opencode spells the same two transports `remote`/`local`, so an
 * opencode remote entry is matched explicitly rather than relying on the
 * `url`-with-no-`command` fallback catching it by accident. Its local entries
 * carry `command` (an array) and no `url`, so they stay correctly non-remote.
 */
export function isRemoteMcpEntry(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const e = value as { type?: unknown; url?: unknown; command?: unknown };
  return (
    e.type === 'http' ||
    e.type === 'sse' ||
    e.type === 'remote' ||
    (typeof e.url === 'string' && e.command === undefined)
  );
}

/**
 * ENG-8359: the `.mcp.json` server keys the agent's current integrations are
 * expected to occupy — the companion input to {@link orphanedRemoteMcpKeys}.
 *
 * Exists as its own function so the reaper's notion of "expected" is the
 * WRITER's rule (`remoteMcpServerKey`) rather than a hand-rolled
 * `map(i => i.definition_id)` at the call site. That mapping was correct only
 * while every remote entry was keyed by definition_id; a NAMED connection is
 * written as `<sanitized-base>-<connection_key>`, which such a set never
 * contains, so the reaper would classify a live second connection as an orphan
 * and delete the server provisioning had just written — a writer/reaper
 * disagreement that produces no error on either side, only a server that keeps
 * vanishing.
 */
export function expectedRemoteMcpKeys(
  integrations: ReadonlyArray<{ definition_id: string; connection_key?: string | null }>,
): Set<string> {
  return new Set(integrations.map((i) => remoteMcpServerKey(i.definition_id, i.connection_key ?? null)));
}

/**
 * ENG-7359: orphaned remote-MCP keys in a `.mcp.json`: remote (HTTP/SSE)
 * servers that are no longer backed by a current integration and are not
 * brokered managed toolkits.
 *
 * When a remote-MCP integration (a DB-catalog `integration_definitions.
 * remote_mcp`, e.g. monday) is removed, its `.mcp.json` key is keyed by the
 * integration's `definition_id`. The core `writeIntegrations` reap only removes
 * keys in its STATIC universe (INTEGRATION_REGISTRY nativeMcp + OAUTH_PROVIDERS
 * + a hardcoded list), so a DB-catalog remote MCP (absent from that universe)
 * was never pruned and lingered forever (the presence-reaper only reaps stdio
 * children; a remote MCP has no child). This computes the stragglers so the
 * caller can `removeMcpServer` them.
 *
 * `expectedIntegrationKeys` is the set of `.mcp.json` server keys for the agent's
 * CURRENT integrations — build it with {@link expectedRemoteMcpKeys}. Native MCPs
 * are stdio (never remote) and brokered toolkits are excluded by
 * `isManagedMcpServerKey`, so neither needs to appear in the set.
 */

export function orphanedRemoteMcpKeys(
  mcpServers: Record<string, unknown>,
  expectedIntegrationKeys: Set<string>,
): string[] {
  const orphans: string[] = [];
  for (const [key, entry] of Object.entries(mcpServers)) {
    if (expectedIntegrationKeys.has(key)) continue; // backed by a live integration
    if (isManagedMcpServerKey(key)) continue; // brokered toolkit (own convergence + prune)
    if (!isRemoteMcpEntry(entry)) continue; // stdio channel/plugin/native (preserve)
    orphans.push(key);
  }
  return orphans;
}
